import React, { FC, useState } from 'react';
import { reduxForm, formValueSelector, change } from 'redux-form';
import ContinueButton from '@components/_shared/Continue/Button';
import { FormPageTopPart } from '@components/_shared/FormPageTopPart/FormPageTopPart';
import { connect, useDispatch } from 'react-redux';
import { useForm } from 'react-hook-form';
import { Form } from '@components/shadcn/components/ui/form';
import { RootState } from '@store/reducers';
import { Helmet } from 'react-helmet';
import { Button, Stack, Text, TextAndButtonGroup } from 'shared-components';
import withOnSubmit from '@components/Apply/withOnSubmit/withOnSubmit';
import { OwnerFields } from '@components/Apply/OwnersMoreDetailsAboutYou/OwnerFields';
import { getNumberOfOwners } from '@components/Apply/OwnersMoreDetailsAboutYou/getNumberOfOwners';
import {
  ApplyWizardSteps,
  STEP_BUSINESS_OWNERS,
  STEP_EQ_LEAD_OWNERS_INFORMATION,
} from '@components/ApplyWizard/ApplyWizardSteps';
import { reduxFormZodValidate } from '@utils/reduxFormZodValidate';
import { useApplyWizardContext } from '../../../context/ApplyWizardContext';
import withLeadOnSubmit from '@components/Apply/withOnSubmit/withLeadOnSubmit';
import ensureNotLoggedIn from '../../../hooks/ensureNotLoggedIn';
import { EQLeadOwnerFields } from '@components/EQLeadFlow/Step3/EQLeadOwnerFields';
import { ensureLeadIds } from '../../../hooks/ensureLeadIds';
import { EQFlowFooter } from '../_shared/EQFlowFooter';

type FurtherBusinessDetailsProps = {
  loanType: string;
  handleSubmit: () => void;
  invalid: boolean;
  ownersCountStart?: number;
  saving?: boolean;
};

const EQLeadOwnersStepWrapper2: FC<FurtherBusinessDetailsProps> = ({
  handleSubmit,
  invalid,
  ownersCountStart,
  saving: savingOnSubmit,
}) => {
  const { saving } = useApplyWizardContext();
  const form = useForm();
  const [ownersCount, setOwnersCount] = useState<number>(ownersCountStart ?? 1);
  const dispatch = useDispatch();

  return (
    <div>
      <Helmet>
        <title>More Details About You</title>
      </Helmet>
      <div className="w-full flex flex-col gap-[20px] mt-[16px]">
        <Text variant="subheadline-sm2" className="!text-[16px] !font-extended py-[20px] !leading-[100%]">
          YOUR APPLICATION IS ALMOST COMPLETE
        </Text>
        <Text variant="subheadline-sm2" className="!text-[20px] !font-standard !leading-[30px]">
          Owner Information
        </Text>
        <Form {...form}>
          <form onSubmit={handleSubmit}>
            <Stack gapPx="40px">
              <Stack gapPx="5px" justifyItems="center" templateColumns="1fr" fullWidth>
                <Stack gapPx="5px" justifyItems="center" templateColumns="1fr" fullWidth>
                  {Array.from({ length: ownersCount }, (_, index) => index + 1).map((ownerNr) => (
                    <>
                      {ownerNr > 1 && (
                        <Stack
                          className="my-[10px] max-w-[592px]"
                          flow="column"
                          gapPx="50px"
                          justifyItems="center"
                          templateColumns="1fr"
                          justifyContent="space-between"
                          fullWidth
                        >
                          <div className="border-charcoal w-full">
                            <Text variant="subheadline-sm">Owner #{ownerNr}</Text>
                            <span></span>
                          </div>
                          {ownerNr === ownersCount && (
                            <TextAndButtonGroup
                              buttonContent="-"
                              text="Remove"
                              buttonProps={{
                                type: 'button',
                                className: 'h-[35px] w-[35px] p-0 !bg-transparent',
                              }}
                              onClick={() => {
                                dispatch(change('application', `owner_${ownersCount}_id`, undefined));
                                dispatch(change('application', `owner_${ownersCount}_title`, undefined));
                                dispatch(change('application', `owner_${ownersCount}_ownership`, undefined));
                                dispatch(change('application', `owner_${ownersCount}_ssn_masked`, undefined));
                                dispatch(change('application', `owner_${ownersCount}_dob`, undefined));
                                dispatch(change('application', `owner_${ownersCount}_dob_day`, undefined));
                                dispatch(change('application', `owner_${ownersCount}_dob_month`, undefined));
                                dispatch(change('application', `owner_${ownersCount}_dob_year`, undefined));
                                dispatch(change('application', `owner_${ownersCount}_ssn`, undefined));
                                dispatch(change('application', `owner_${ownersCount}_street`, undefined));
                                dispatch(change('application', `owner_${ownersCount}_city`, undefined));
                                dispatch(change('application', `owner_${ownersCount}_zip`, undefined));
                                dispatch(change('application', `owner_${ownersCount}_suite`, undefined));
                                dispatch(change('application', `owner_${ownersCount}_state`, undefined));
                                dispatch(change('application', `owner_${ownersCount}_first`, undefined));
                                dispatch(change('application', `owner_${ownersCount}_last`, undefined));
                                dispatch(change('application', `owner_${ownersCount}_mobile`, undefined));
                                dispatch(change('application', `owner_${ownersCount}_email`, undefined));
                                setOwnersCount(ownersCount - 1);
                              }}
                            />
                          )}
                        </Stack>
                      )}
                      <EQLeadOwnerFields withAllFields={ownerNr > 1} ownerNr={ownerNr} />
                    </>
                  ))}
                </Stack>
                {ownersCount < 2 && (
                  <TextAndButtonGroup
                    buttonContent="+"
                    text="Add Additional Owner"
                    onClick={() => setOwnersCount(ownersCount + 1)}
                    buttonProps={{
                      type: 'button',
                      className: 'border-none h-[35px] w-[35px] p-0',
                    }}
                    stackProps={{
                      className: 'max-w-[592px]',
                    }}
                  />
                )}
              </Stack>

              <div className="w-full flex flex-col items-center justify-center gap-[10px] mx-auto">
                <Button
                  variant="primary-green"
                  color="primary"
                  type="submit"
                  disabled={invalid || saving}
                  isLoading={saving || savingOnSubmit}
                  data-testid="info_continue"
                  className="!max-w-[592px] !h-[50px] border-[1px] border-spark2 !py-[12.5px] font-condensed leading-[120%] px-[50px] !text-[16px]"
                >
                  CONTINUE
                </Button>
                <Text variant="body-form-label" className="!text-[16px] !leading-[18px] !font-condensed text-charcoal2">
                  *NO HARD CREDIT CHECKS
                </Text>
              </div>
            </Stack>
          </form>

          <EQFlowFooter />
        </Form>
      </div>
    </div>
  );
};

const EQLeadOwnersStepWrapper1 = reduxForm<FurtherBusinessDetailsProps, FurtherBusinessDetailsProps>({
  form: 'application',
  destroyOnUnmount: false,
  forceUnregisterOnUnmount: true,
  shouldValidate: () => true,
  validate: reduxFormZodValidate(ApplyWizardSteps[STEP_EQ_LEAD_OWNERS_INFORMATION].zodSchema),
})(EQLeadOwnersStepWrapper2);

const appSelector = formValueSelector('application');

const mapStateToProps = (state: RootState) => {
  return {
    ownersCountStart: getNumberOfOwners(state),
  };
};

export const EQLeadOwnersStep = ensureLeadIds(
  ensureNotLoggedIn(
    withLeadOnSubmit(connect(mapStateToProps)(EQLeadOwnersStepWrapper1), STEP_EQ_LEAD_OWNERS_INFORMATION)
  )
);
